/**
 * Ruin claim mechanics — pure game logic.
 * Players claim derelict structures for team benefits.
 * No Three.js imports. Deterministic given same inputs.
 */

import type { PlayerState, PlanetState, RuinState, GameState, Vec3 } from '../types';
import { ruinJob } from './body';
import { vecSub, vecLength } from '../math';
import { getPlayerWorldPosition, getRuinWorldPosition } from './gameState';
import {
  CLAIM_MOVEMENT_COST,
  CLAIM_RANGE,
  CLAIM_TURNS_REQUIRED,
  CLAIM_BREAK_RANGE,
} from '../constants';

// =============================================================================
// HELPERS
// =============================================================================

// Terrain-exact positions from gameState (these used to be base-radius copies)
function getPlayerWorldPos(player: PlayerState, planets: PlanetState[]): Vec3 {
  return getPlayerWorldPosition(player, planets);
}

// =============================================================================
// CAN CLAIM
// =============================================================================

/**
 * Check if a player can initiate a claim on a ruin.
 * Requires: alive, grounded, same planet, within range, enough movement, not own team.
 */
export function canClaim(
  player: PlayerState,
  ruin: RuinState,
  planets: PlanetState[],
): boolean {
  if (!player.alive || player.airborne) return false;
  if (player.planetIndex !== ruin.planetIndex) return false;
  if (player.movementRemaining < CLAIM_MOVEMENT_COST) return false;

  // Can't re-claim your own team's claimed ruin
  if (ruin.claimTeamIndex !== undefined && ruin.claimTeamIndex >= 0
      && ruin.claimTeamIndex === player.teamIndex && ruin.claimBenefitActive) {
    return false;
  }

  // Check distance on surface
  const playerPos = getPlayerWorldPos(player, planets);
  const ruinPos = getRuinWorldPosition(ruin, planets);
  const dist = vecLength(vecSub(playerPos, ruinPos));

  return dist <= CLAIM_RANGE;
}

// =============================================================================
// START CLAIM
// =============================================================================

/**
 * Initiate a claim on a ruin. Sets claimTeamIndex and claimProgress=1.
 * Returns updated GameState with the ruin claim started and movement deducted.
 */
export function startClaim(
  state: GameState,
  playerId: number,
  ruinId: number,
): GameState {
  const player = state.players.find(p => p.id === playerId);
  const ruinIdx = state.ruins.findIndex(r => r.id === ruinId);
  if (!player || ruinIdx < 0) return state;

  const updatedRuins = [...state.ruins];
  updatedRuins[ruinIdx] = {
    ...updatedRuins[ruinIdx],
    claimTeamIndex: player.teamIndex,
    claimProgress: 1,
    claimBroken: false,
    claimBenefitActive: false,
  };

  const updatedPlayers = state.players.map(p =>
    p.id === playerId
      ? { ...p, movementRemaining: p.movementRemaining - CLAIM_MOVEMENT_COST }
      : p
  );

  return { ...state, ruins: updatedRuins, players: updatedPlayers };
}

// =============================================================================
// TICK CLAIMS
// =============================================================================

/**
 * Advance claim progress for all claimed ruins.
 * Increments if a team member is nearby, otherwise pauses.
 * Completes when claimProgress >= CLAIM_TURNS_REQUIRED.
 */
export function tickClaims(
  ruins: RuinState[],
  players: PlayerState[],
  planets: PlanetState[],
): RuinState[] {
  return ruins.map(ruin => {
    if (ruin.claimTeamIndex === undefined || ruin.claimTeamIndex < 0) return ruin;
    if (ruin.claimBenefitActive) return ruin; // already complete
    if (ruin.claimBroken) return ruin;

    const claimProgress = ruin.claimProgress ?? 0;

    // Check if any alive teammate is on the same planet
    const teamOnPlanet = players.some(p =>
      p.alive && !p.airborne
      && p.teamIndex === ruin.claimTeamIndex
      && p.planetIndex === ruin.planetIndex
    );

    if (!teamOnPlanet) return ruin; // pause, don't increment

    const newProgress = claimProgress + 1;
    if (newProgress >= CLAIM_TURNS_REQUIRED) {
      return { ...ruin, claimProgress: newProgress, claimBenefitActive: true };
    }

    return { ...ruin, claimProgress: newProgress };
  });
}

// =============================================================================
// CONTESTATION
// =============================================================================

/**
 * Check for enemy presence near claimed ruins. Breaks claim if enemy is within range.
 */
export function processClaimContestations(
  ruins: RuinState[],
  players: PlayerState[],
  planets: PlanetState[],
): RuinState[] {
  return ruins.map(ruin => {
    if (ruin.claimTeamIndex === undefined || ruin.claimTeamIndex < 0) return ruin;

    const ruinPos = getRuinWorldPosition(ruin, planets);

    const contested = players.some(p => {
      if (!p.alive || p.airborne) return false;
      if (p.teamIndex === ruin.claimTeamIndex) return false;
      if (p.planetIndex !== ruin.planetIndex) return false;

      const playerPos = getPlayerWorldPos(p, planets);
      const dist = vecLength(vecSub(playerPos, ruinPos));
      return dist <= CLAIM_BREAK_RANGE;
    });

    if (contested) {
      return { ...ruin, claimBroken: true, claimBenefitActive: false };
    }

    return ruin;
  });
}

// =============================================================================
// CLAIM BENEFITS
// =============================================================================

export interface ClaimBenefit {
  type: string;
  value: number;
}

/**
 * Get the benefit a claimed ruin provides.
 */
export function getClaimBenefit(ruin: RuinState): ClaimBenefit | null {
  if (!ruin.claimBenefitActive) return null;

  // X18: the ruin's job (constants/body.ts RUIN_JOBS) says what a claim grants
  const b = ruinJob(ruin).benefit;
  return b ? { ...b } : null;
}

/**
 * Apply ongoing claim benefits on turn start: CBP income from refineries, HP regen from habitats.
 */
export function applyClaimBenefits(state: GameState): GameState {
  let updatedPlayers = [...state.players];
  let changed = false;

  for (const ruin of state.ruins) {
    const benefit = getClaimBenefit(ruin);
    if (!benefit) continue;

    if (benefit.type === 'cbpIncome') {
      // Give CBP to all alive players on the claiming team
      updatedPlayers = updatedPlayers.map(p => {
        if (p.alive && p.teamIndex === ruin.claimTeamIndex) {
          changed = true;
          return { ...p, cbp: p.cbp + benefit.value };
        }
        return p;
      });
    } else if (benefit.type === 'healthRegen') {
      // Regen HP for team members on the ruin's planet
      updatedPlayers = updatedPlayers.map(p => {
        if (p.alive && p.teamIndex === ruin.claimTeamIndex
            && p.planetIndex === ruin.planetIndex && p.health < 100) {
          changed = true;
          return { ...p, health: Math.min(100, p.health + benefit.value) };
        }
        return p;
      });
    }
  }

  return changed ? { ...state, players: updatedPlayers } : state;
}
